import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendUp, Buildings, House, Hammer, CheckCircle, Lightning, ShoppingBag, ChatCircle, ShieldCheck, Funnel, WarningCircle, Star, BellRinging } from "@phosphor-icons/react";

// Admin Insights — aggregated read-only view of platform health.
// Sits inside AdminPage as a tab. Sections map 1:1 to /api/admin/insights/* endpoints.
export default function InsightsPanel() {
  const [overview, setOverview] = useState(null);
  const [categories, setCategories] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [community, setCommunity] = useState(null);
  const [future, setFuture] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [stalled, setStalled] = useState(null);
  const [help, setHelp] = useState(null);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ov, cat, fn, cmp, ai, co, fr, ou, fb, st, hl, tm] = await Promise.all([
          api.get("/admin/insights/overview").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/categories").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/funnel").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/completion").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/ai-usage").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/community").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/future-requests").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/outcomes").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/feedback").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/stalled").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/help-assistance").then((r) => r.data).catch(() => null),
          api.get("/admin/insights/terms-acceptance").then((r) => r.data).catch(() => null),
        ]);
        setOverview(ov); setCategories(cat); setFunnel(fn); setCompletion(cmp);
        setAiUsage(ai); setCommunity(co); setFuture(fr); setOutcomes(ou);
        setFeedback(fb); setStalled(st); setHelp(hl); setTerms(tm);
      } catch (e) {
        toast.error("Couldn't load insights");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-sm text-timber-500">Loading insights…</p>;

  return (
    <div className="space-y-4" data-testid="admin-insights">
      {/* 1. Overview */}
      <Card title="Overview" icon={TrendUp} testid="insights-overview">
        {overview ? (
          <div className="grid grid-cols-2 gap-1.5">
            <Metric label="Total users" value={overview.total_users} testid="ov-total-users" />
            <Metric label="Approved" value={overview.approved_users} testid="ov-approved" />
            <Metric label="Active (7d)" value={overview.active_users_7d} testid="ov-active-7d" />
            <Metric label="Active (30d)" value={overview.active_users_30d} testid="ov-active-30d" />
            <Metric label="Households" value={overview.total_households} testid="ov-households" />
            <Metric label="Total projects" value={overview.total_projects} testid="ov-total-projects" />
            <Metric label="In progress" value={overview.projects_in_progress} testid="ov-in-progress" />
            <Metric label="Completed" value={overview.projects_completed} testid="ov-completed" />
            <Metric label="Avg projects / user" value={overview.avg_projects_per_user} testid="ov-avg-per-user" />
            <Metric label="Avg completion %" value={`${overview.avg_completion_pct}%`} testid="ov-avg-completion" />
          </div>
        ) : <Empty />}
      </Card>

      {/* 2. Top Categories */}
      <Card title="Top categories" icon={Hammer} testid="insights-categories">
        {categories?.items?.length ? (
          <ul className="space-y-1.5">
            {categories.items.map((c) => (
              <li key={c.category} className="flex items-center justify-between text-sm" data-testid={`cat-${c.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <span className="text-timber-900">{c.category}</span>
                <span className="font-mono font-bold text-timber-700">{c.count}</span>
              </li>
            ))}
          </ul>
        ) : <Empty />}
      </Card>

      {/* 14. Funnel */}
      <Card title="Project funnel" icon={Funnel} testid="insights-funnel">
        {funnel?.stages?.length ? (
          <ul className="space-y-1.5">
            {funnel.stages.map((s, i) => {
              const prev = funnel.stages[0]?.count || 1;
              const pct = Math.round((s.count / Math.max(1, prev)) * 100);
              return (
                <li key={s.key} data-testid={`funnel-${s.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-timber-900">{s.label}</span>
                    <span className="font-mono font-bold text-timber-700">{s.count} <span className="text-xs text-timber-500">({pct}%)</span></span>
                  </div>
                  {i > 0 && (
                    <div className="h-1.5 bg-stone_border rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-denim" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : <Empty />}
      </Card>

      {/* 11. Completion analytics */}
      <Card title="Completion rate" icon={CheckCircle} testid="insights-completion">
        {completion?.items?.length ? (
          <>
            <p className="text-xs text-timber-500 mb-2">Overall: <span className="font-mono font-bold text-timber-900">{completion.overall_completion_rate_pct}%</span></p>
            <ul className="space-y-1">
              {completion.items.slice(0, 8).map((c) => (
                <li key={c.category} className="text-sm flex items-center justify-between">
                  <span className="text-timber-900 truncate">{c.category}</span>
                  <span className="font-mono text-timber-700">{c.complete}/{c.total} ({c.completion_rate_pct}%)</span>
                </li>
              ))}
            </ul>
          </>
        ) : <Empty />}
      </Card>

      {/* 10. AI usage */}
      <Card title="AI usage (last 24h)" icon={Lightning} testid="insights-ai-usage">
        {aiUsage ? (
          <>
            <p className="text-sm text-timber-900 mb-2"><span className="font-mono font-bold">{aiUsage.total_calls}</span> total calls</p>
            {aiUsage.by_kind?.length > 0 && (
              <ul className="space-y-1 text-sm">
                {aiUsage.by_kind.map((k) => (
                  <li key={k.kind} className="flex items-center justify-between">
                    <span className="text-timber-700">{k.kind}</span>
                    <span className="font-mono font-bold text-timber-900">{k.calls}</span>
                  </li>
                ))}
              </ul>
            )}
            {aiUsage.top_households?.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-timber-700 mb-1">Top households</p>
                <ul className="space-y-1 text-xs">
                  {aiUsage.top_households.slice(0, 5).map((h) => (
                    <li key={h.household_id} className="flex items-center justify-between">
                      <span className="text-timber-700 truncate">{h.household_name || h.household_id.slice(0, 8)}</span>
                      <span className="font-mono font-bold text-timber-900">{h.calls}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : <Empty />}
      </Card>

      {/* 12. AI assistance analytics */}
      <Card title="Need Help / Check My Work" icon={ChatCircle} testid="insights-help">
        {help ? (
          <>
            <p className="text-sm text-timber-900 mb-2"><span className="font-mono font-bold">{help.total_help_records}</span> calls in {help.window_days} days</p>
            <ul className="space-y-1 text-sm">
              {Object.entries(help.by_assessment || {}).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between">
                  <span className="text-timber-700">{k}</span>
                  <span className="font-mono font-bold text-timber-900">{v}</span>
                </li>
              ))}
            </ul>
          </>
        ) : <Empty />}
      </Card>

      {/* 9. Stalled projects */}
      <Card title={`Stalled projects (>${stalled?.cutoff_days || 14}d)`} icon={WarningCircle} testid="insights-stalled">
        {stalled?.items?.length ? (
          <ul className="space-y-2 text-sm">
            {stalled.items.slice(0, 10).map((p) => (
              <li key={p.id} className="border-b border-stone_border pb-1.5 last:border-0">
                <p className="font-bold text-timber-900 truncate">{p.title}</p>
                <p className="text-[11px] text-timber-500 font-mono">{p.user_email} · {p.category} · {p.days_since_update}d ago</p>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-timber-500" data-testid="stalled-empty">No stalled projects — everyone&apos;s moving.</p>}
      </Card>

      {/* 7. Community knowledge */}
      <Card title="Community signals" icon={Buildings} testid="insights-community">
        {community ? (
          <div className="space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-timber-700 mb-1">Most-needed materials</p>
              {community.most_needed_materials?.length ? (
                <ul className="space-y-1 text-sm">
                  {community.most_needed_materials.slice(0, 6).map((m) => (
                    <li key={m.name} className="flex items-center justify-between">
                      <span className="text-timber-900 truncate">{m.name}</span>
                      <span className="font-mono font-bold text-timber-700">{m.count}</span>
                    </li>
                  ))}
                </ul>
              ) : <Empty />}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-timber-700 mb-1">Common blockers</p>
              {community.common_blocked_steps?.length ? (
                <ul className="space-y-1 text-sm">
                  {community.common_blocked_steps.slice(0, 6).map((s) => (
                    <li key={s.title} className="flex items-center justify-between">
                      <span className="text-timber-900 truncate">{s.title}</span>
                      <span className="font-mono font-bold text-timber-700">{s.count}</span>
                    </li>
                  ))}
                </ul>
              ) : <Empty />}
            </div>
          </div>
        ) : <Empty />}
      </Card>

      {/* 6. Future request intelligence */}
      <Card title="Future requests" icon={House} testid="insights-future">
        {future ? (
          <>
            <p className="text-sm text-timber-900 mb-2"><span className="font-mono font-bold">{future.total}</span> total</p>
            {future.by_type?.length > 0 && (
              <ul className="space-y-1 text-sm">
                {future.by_type.map((t) => (
                  <li key={t.type} className="flex items-center justify-between">
                    <span className="text-timber-700">{t.type}</span>
                    <span className="font-mono font-bold text-timber-900">{t.count}</span>
                  </li>
                ))}
              </ul>
            )}
            {future.latest_feedback?.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-timber-700 mb-1">Latest feedback</p>
                <ul className="space-y-1.5 text-xs">
                  {future.latest_feedback.slice(0, 4).map((f, i) => (
                    <li key={i} className="text-timber-700 italic line-clamp-2">{`"${f.feedback}"`}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : <Empty />}
      </Card>

      {/* 8. Project outcomes */}
      <Card title="Project ratings" icon={Star} testid="insights-outcomes">
        {outcomes ? (
          outcomes.total > 0 ? (
            <>
              <p className="text-sm text-timber-900 mb-2">Avg rating: <span className="font-mono font-bold">{outcomes.avg_rating}</span> / 5 ({outcomes.total} reviews)</p>
              <ul className="space-y-1 text-sm">
                {[1, 2, 3, 4, 5].map((r) => (
                  <li key={r} className="flex items-center justify-between">
                    <span className="text-timber-700">{"★".repeat(r)}</span>
                    <span className="font-mono font-bold text-timber-900">{outcomes.by_rating[r] || 0}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <p className="text-sm text-timber-500">No ratings yet.</p>
        ) : <Empty />}
      </Card>

      {/* 13. Terms acceptance */}
      <Card title="Terms acceptance" icon={ShieldCheck} testid="insights-terms">
        {terms ? (
          <p className="text-sm text-timber-900"><span className="font-mono font-bold">{terms.users_accepted}</span> of <span className="font-mono font-bold">{terms.approved_users}</span> approved users have accepted (<span className="font-mono font-bold">{terms.acceptance_pct}%</span>)</p>
        ) : <Empty />}
      </Card>

      {/* 15. Feedback / bugs */}
      <Card title="Feedback inbox" icon={BellRinging} testid="insights-feedback">
        {feedback?.items?.length ? (
          <ul className="space-y-2 text-sm">
            {feedback.items.slice(0, 10).map((f) => (
              <li key={f.id} className="border-b border-stone_border pb-1.5 last:border-0" data-testid={`feedback-${f.id}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold text-timber-900 truncate">{f.title}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${f.kind === "bug" ? "bg-block text-white" : "bg-masking text-timber-900"}`}>{f.kind}</span>
                </div>
                <p className="text-xs text-timber-700 line-clamp-2 mt-0.5">{f.body}</p>
                <p className="text-[10px] text-timber-500 font-mono mt-0.5">{f.email} · {f.status}</p>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-timber-500" data-testid="feedback-empty">No feedback yet.</p>}
      </Card>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <section data-testid={testid} className="tactile-card p-3.5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} weight="duotone" className="text-timber-700" />
        <h2 className="font-heading text-lg font-bold text-timber-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-kraft border border-stone_border rounded-lg px-2.5 py-2">
      <p className="text-[10px] uppercase tracking-wider font-bold text-timber-700">{label}</p>
      <p className="font-heading text-xl font-bold text-timber-900">{value ?? 0}</p>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-timber-500">No data yet.</p>;
}
